import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useHospitalizationSectors,
  useCreateSetor,
  useCreateBaia,
  useDeleteSetor,
  useDeleteBaia,
  type Setor,
} from "@/hooks/useHospitalizationSectors";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

type DeleteTarget = { type: "setor" | "baia"; id: string } | null;

export default function HospitalizationSettings() {
  const { t } = useTranslation("hospitalization");
  const { data: setores = [], isLoading } = useHospitalizationSectors();
  const createSetor = useCreateSetor();
  const createBaia = useCreateBaia();
  const deleteSetor = useDeleteSetor();
  const deleteBaia = useDeleteBaia();

  const [novoSetorNome, setNovoSetorNome] = useState("");
  const [novaBaiaNome, setNovaBaiaNome] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);

  async function handleCreateSetor() {
    if (!novoSetorNome.trim()) return;
    try {
      await createSetor.mutateAsync({ nome: novoSetorNome.trim() });
      toast.success(t("success.setorSaved"));
      setNovoSetorNome("");
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  async function handleCreateBaia(setorId: string) {
    const nome = (novaBaiaNome[setorId] ?? "").trim();
    if (!nome) return;
    try {
      await createBaia.mutateAsync({ setor_id: setorId, nome });
      toast.success(t("success.baiaSaved"));
      setNovaBaiaNome((prev) => ({ ...prev, [setorId]: "" }));
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "setor") {
        await deleteSetor.mutateAsync(deleteTarget.id);
      } else {
        await deleteBaia.mutateAsync(deleteTarget.id);
      }
    } catch {
      toast.error(t("errors.saveFailed"));
    } finally {
      setDeleteTarget(null);
    }
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold">{t("settings.title")}</h1>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("settings.novoSetor")}</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input
            value={novoSetorNome}
            onChange={(e) => setNovoSetorNome(e.target.value)}
            placeholder={t("settings.nomeSetor")}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleCreateSetor();
            }}
          />
          <Button onClick={() => void handleCreateSetor()} disabled={createSetor.isPending}>
            <Plus className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : setores.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("settings.semSetores")}</p>
      ) : (
        setores.map((setor: Setor) => (
          <Card key={setor.id}>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">{setor.nome}</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => setDeleteTarget({ type: "setor", id: setor.id })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {setor.baias.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("settings.semBaias")}</p>
              )}
              {setor.baias.map((baia) => (
                <div
                  key={baia.id}
                  className="flex items-center justify-between bg-muted/40 rounded px-3 py-1.5"
                >
                  <span className="text-sm">{baia.nome}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => setDeleteTarget({ type: "baia", id: baia.id })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Input
                  value={novaBaiaNome[setor.id] ?? ""}
                  onChange={(e) =>
                    setNovaBaiaNome((prev) => ({ ...prev, [setor.id]: e.target.value }))
                  }
                  placeholder={t("settings.nomeBaia")}
                  className="text-sm"
                  inputSize="sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleCreateBaia(setor.id);
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void handleCreateBaia(setor.id)}
                  disabled={createBaia.isPending}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings.confirmarExclusao")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "setor"
                ? t("settings.excluirSetor")
                : t("settings.excluirBaia")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("settings.cancelar")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()}>
              {t("settings.excluir")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
